// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {PRODUCT_NAME} from '@app/features/app/config/ProductConstants';
import {buildGatewayIntentFlags, type DeveloperApplication} from '@app/features/devtools/models/DeveloperApplication';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import ApplicationsTabState from '@app/features/user/components/modals/tabs/applications_tab/ApplicationsTabState';
import styles from '@app/features/user/components/modals/tabs/applications_tab/application_detail/ApplicationDetail.module.css';
import {SectionCard} from '@app/features/user/components/modals/tabs/applications_tab/application_detail/ApplicationDetailSectionCard';
import {pushApiErrorModal} from '@app/lib/forms';
import {allowsIntent, type PrivilegedIntent} from '@fluxer/constants/src/BotConstants';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useState} from 'react';

const PRIVILEGED_GATEWAY_INTENTS_DESCRIPTOR = msg({
	message: 'Privileged gateway intents',
	comment: 'Section title in the developer application detail for the bot gateway intent switches.',
});
const PRIVILEGED_GATEWAY_INTENTS_SUBTITLE_DESCRIPTOR = msg({
	message: 'Extra data your bot can receive. Turn on only what it needs. Changing these reconnects your bot.',
	comment: 'Section subtitle for the privileged gateway intent switches. Keep the tone plain and specific.',
});
const SERVER_MEMBERS_INTENT_DESCRIPTOR = msg({
	message: 'Server members',
	comment: 'Switch label for the server members gateway intent of a bot. Keep it concise.',
});
const SERVER_MEMBERS_INTENT_HELP_DESCRIPTOR = msg({
	message: 'Receive member join, update and leave events, and request the full member list of a community.',
	comment: 'Description under the server members gateway intent switch.',
});
const PRESENCE_INTENT_DESCRIPTOR = msg({
	message: 'Presence',
	comment: 'Switch label for the presence gateway intent of a bot (online status and activity). Keep it concise.',
});
const PRESENCE_INTENT_HELP_DESCRIPTOR = msg({
	message: 'Receive online status and activity updates for members of the communities your bot is in.',
	comment: 'Description under the presence gateway intent switch.',
});
const MESSAGE_CONTENT_INTENT_DESCRIPTOR = msg({
	message: 'Message content',
	comment: 'Switch label for the message content gateway intent of a bot. Keep it concise.',
});
const MESSAGE_CONTENT_INTENT_HELP_DESCRIPTOR = msg({
	message:
		'Read the text, embeds, attachments and components of messages. Without it, your bot still sees its own messages, messages that mention it and DMs.',
	comment: 'Description under the message content gateway intent switch.',
});
const PENDING_APPROVAL_DESCRIPTOR = msg({
	message: 'Pending approval',
	comment: 'Badge next to a bot gateway intent that was requested and waits for review by the staff.',
});
const INTENTS_NEED_APPROVAL_DESCRIPTOR = msg({
	message: 'Your bot is in more than 100 communities, so turning these on needs approval from the {productName} team.',
	comment:
		'Note under the bot gateway intent switches when the bot is in more than 100 communities. {productName} is the app name.',
});
const TURN_OFF_INTENT_TITLE_DESCRIPTOR = msg({
	message: 'Turn off this intent?',
	comment: 'Title of the confirmation shown before turning off a bot gateway intent.',
});
const TURN_OFF_INTENT_BODY_DESCRIPTOR = msg({
	message: "Your bot's connections will be re-established, and events and data that need this intent will stop.",
	comment: 'Body of the confirmation shown before turning off a bot gateway intent.',
});
const TURN_OFF_DESCRIPTOR = msg({
	message: 'Turn off',
	comment: 'Confirm button that turns off a bot gateway intent. Keep it concise.',
});
const COULD_NOT_UPDATE_INTENTS_DESCRIPTOR = msg({
	message: "Couldn't update gateway intents",
	comment: 'Title of the error modal shown when changing a bot gateway intent fails.',
});

const INTENTS: ReadonlyArray<{key: PrivilegedIntent; label: MessageDescriptor; help: MessageDescriptor}> = [
	{key: 'guild_members', label: SERVER_MEMBERS_INTENT_DESCRIPTOR, help: SERVER_MEMBERS_INTENT_HELP_DESCRIPTOR},
	{key: 'presence', label: PRESENCE_INTENT_DESCRIPTOR, help: PRESENCE_INTENT_HELP_DESCRIPTOR},
	{key: 'message_content', label: MESSAGE_CONTENT_INTENT_DESCRIPTOR, help: MESSAGE_CONTENT_INTENT_HELP_DESCRIPTOR},
];

const logger = new Logger('GatewayIntentsSection');

interface GatewayIntentsSectionProps {
	application: DeveloperApplication;
}

export const GatewayIntentsSection: React.FC<GatewayIntentsSectionProps> = ({application}) => {
	const {i18n} = useLingui();
	const [updating, setUpdating] = useState<PrivilegedIntent | null>(null);
	const save = async (intent: PrivilegedIntent, enabled: boolean) => {
		setUpdating(intent);
		try {
			await ApplicationsTabState.updateIntentFlags(
				application.id,
				buildGatewayIntentFlags(application, intent, enabled),
			);
		} catch (err) {
			logger.error('Failed to update gateway intents', err);
			pushApiErrorModal(i18n, err, i18n._(COULD_NOT_UPDATE_INTENTS_DESCRIPTOR));
		} finally {
			setUpdating(null);
		}
	};
	const handleChange = (intent: PrivilegedIntent, enabled: boolean) => {
		if (enabled || !allowsIntent(application.flags, intent)) {
			void save(intent, enabled);
			return;
		}
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(TURN_OFF_INTENT_TITLE_DESCRIPTOR)}
					description={i18n._(TURN_OFF_INTENT_BODY_DESCRIPTOR)}
					primaryText={i18n._(TURN_OFF_DESCRIPTOR)}
					primaryVariant="danger"
					onPrimary={() => save(intent, false)}
					data-flx="user.applications-tab.application-detail.gateway-intents-section.confirm-modal"
				/>
			)),
		);
	};
	const needsApproval =
		application.approximate_guild_count === undefined
			? INTENTS.some(({key}) => allowsIntent(application.intent_flags_pending, key))
			: application.approximate_guild_count > 100;
	return (
		<SectionCard
			title={i18n._(PRIVILEGED_GATEWAY_INTENTS_DESCRIPTOR)}
			subtitle={i18n._(PRIVILEGED_GATEWAY_INTENTS_SUBTITLE_DESCRIPTOR)}
			data-flx="user.applications-tab.application-detail.gateway-intents-section.section-card"
		>
			<div
				className={styles.fieldStack}
				data-flx="user.applications-tab.application-detail.gateway-intents-section.field-stack"
			>
				{INTENTS.map(({key, label, help}) => {
					const enabled = allowsIntent(application.flags, key);
					const pending = !enabled && allowsIntent(application.intent_flags_pending, key);
					return (
						<Switch
							key={key}
							label={
								<span
									className={styles.intentLabel}
									data-flx="user.applications-tab.application-detail.gateway-intents-section.intent-label"
								>
									{i18n._(label)}
									{pending && (
										<span
											className={styles.pill}
											data-flx="user.applications-tab.application-detail.gateway-intents-section.pill"
										>
											{i18n._(PENDING_APPROVAL_DESCRIPTOR)}
										</span>
									)}
								</span>
							}
							description={i18n._(help)}
							value={enabled || pending}
							disabled={updating !== null}
							onChange={(checked) => handleChange(key, checked)}
							data-flx="user.applications-tab.application-detail.gateway-intents-section.switch"
						/>
					);
				})}
				{needsApproval && (
					<p
						className={styles.helperText}
						data-flx="user.applications-tab.application-detail.gateway-intents-section.helper-text"
					>
						{i18n._(INTENTS_NEED_APPROVAL_DESCRIPTOR, {productName: PRODUCT_NAME})}
					</p>
				)}
			</div>
		</SectionCard>
	);
};
